import React, { useEffect, useCallback, useState, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "components/CustomButtons/Button";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";

import EnhancedTableHead, {
  stableSort,
  getComparator
} from "../../common/EnhancedTableHead";
import Loading from "../../common/Loading";

import { actionCreators as actionCreatorsChapter } from "../../../_actions/chapterActions";

import { yearList } from "../../../utils";
import { PAGE_SIZE } from "../../../constants";
import { twoDigit } from "../../../utils";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  cursorPointer: {
    cursor: "pointer"
  },
  fileIcon: {
    width: "1.5rem"
  },
  fiscalYear: {
    minWidth: "120px"
  }
}));

const headCells = [
  {
    id: "year",
    numeric: false,
    disablePadding: false,
    label: "Year"
  },
  {
    id: "updatedDate",
    disablePadding: false,
    label: "Updated Date"
  },
  {
    id: "submittedBy",
    disablePadding: false,
    label: "Submitted By"
  }
];

const ManageChapterBudgets = props => {
  const classes = useStyles();
  const [fiscalYear, setFiscalYear] = useState(-1);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("chapterName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(localStorage.getItem("chapterBudgetPageSize")) || PAGE_SIZE
  );
  const history = useHistory();

  const {
    getChapterBudgets,
    resetActiveChapterBudget,
    chapterBudgets,
    totalLength,
    isFetching,
    isFetched,
    user
  } = props;

  useEffect(() => {
    (async () => {
      if (!isFetching.CHAPTER_BUDGETS && !isFetched.CHAPTER_BUDGETS) {
        await getChapterBudgets();
      }
    })();
  }, []);

  const filteredChapterBudgets = useMemo(
    () =>
      fiscalYear === -1
        ? chapterBudgets
        : chapterBudgets.filter(item => item.year === fiscalYear),
    [fiscalYear, chapterBudgets]
  );

  const handleEditTopic = useCallback(
    row => async () => {
      await resetActiveChapterBudget();
      history.push(`/chapter/budget/${row.id}`);
    },
    [history]
  );

  const handleSubmitBudget = useCallback(() => {
    (async () => {
      await resetActiveChapterBudget();
      history.push("/chapter/budget/add");
    })();
  }, [history]);

  const handleFiscalYear = useCallback(({ target: { value } }) => {
    setFiscalYear(value);
  }, []);

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    localStorage.setItem("chapterBudgetPageSize", value);
    setPage(0);
  }, []);

  const paginate = useCallback(
    list => {
      return list.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    },
    [page, rowsPerPage]
  );

  const formattedDate = param => {
    const date = new Date(param);
    const amPm = date.getHours() >= 12 ? "pm" : "am";
    const hour = date.getHours() > 12 ? date.getHours() - 12 : date.getHours();
    return `${date.getFullYear()}-${twoDigit(date.getMonth() + 1)}-${twoDigit(
      date.getDate()
    )} ${hour}:${date.getMinutes()} ${amPm}`;
  };

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">
              Chapter Budget - {user.chapter.name}
            </Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Paper className={classes.paper}>
            <Grid container justify="space-between">
              <Grid item className={classes.marginBottom}>
                <FormControl variant="outlined" className={classes.fiscalYear}>
                  <InputLabel id="demo-simple-select-outlined-label">
                    Fiscal Year
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-outlined-label"
                    value={fiscalYear}
                    onChange={handleFiscalYear}
                    label="Fiscal Year"
                    MenuProps={{ classes: { paper: classes.menuPaper } }}
                  >
                    <MenuItem value={-1}>All</MenuItem>
                    {yearList.map(value => (
                      <MenuItem value={value} key={`fiy-${value}`}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Button color="success" onClick={handleSubmitBudget}>
              Submit Annual Budget
            </Button>
            <TableContainer>
              <Table
                className={classes.table}
                aria-labelledby="tableTitle"
                size="medium"
                aria-label="enhanced table"
              >
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={totalLength}
                  headCells={headCells}
                />
                <TableBody>
                  {isFetching.CHAPTER_BUDGETS ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Loading />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginate(
                      stableSort(
                        filteredChapterBudgets,
                        getComparator(order, orderBy)
                      )
                    ).map((row, index) => {
                      const labelId = `enhanced-table-row-${index}`;
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={labelId}
                          onClick={handleEditTopic(row)}
                        >
                          <TableCell component="th" scope="row">
                            {row.year}
                          </TableCell>
                          <TableCell>
                            {formattedDate(row.updatedDate)}
                          </TableCell>
                          <TableCell>{row.submittedBy}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={
                (filteredChapterBudgets && filteredChapterBudgets.length) || 0
              }
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Paper>
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  chapterBudgets: state.chapterReducer.chapterBudgets,
  totalLength: state.chapterReducer.totalChapters,
  isFetching: state.chapterReducer.isFetching,
  isFetched: state.chapterReducer.isFetched,
  user: state.userReducer.user
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsChapter }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ManageChapterBudgets);
